import React from "react";
import PropTypes from "prop-types";
import {Line} from "react-chartjs-2";
import {Box, CircularProgress} from "@mui/material";

import {blueLine, purpleLine, defaultOptions} from "config/graph/defaultLineGraphConfig";
import {black500, greyishBlue100, greyishBlue600} from "assets/jss/main";
import ChartLegend from "./ChartLegend";
import {UserAgentUtils} from "../../../../../../../utils/useragent";

function SalesLineChart({
    title,
    kpiValue,
    subtitle,
    onlineData,
    physicalData,
    onlineLegend,
    physicalLegend,
    unit,
    unitParser,
    unitTooltip,
    loading,
}) {
    const isMobile = UserAgentUtils.isMobile();

    function buildDatasets() {
        const cloneBlue = JSON.parse(JSON.stringify(blueLine));
        cloneBlue.label = onlineLegend;
        cloneBlue.data = Object.entries(onlineData ?? {}).map(([x, y]) => ({x, y}));

        const datasets = [cloneBlue];

        physicalData = null; // Disable physical data for now

        if (physicalData && Object.keys(physicalData).length > 0) {
            const clonePurple = JSON.parse(JSON.stringify(purpleLine));

            clonePurple.label = physicalLegend;
            clonePurple.data = Object.entries(physicalData).map(([x, y]) => ({x, y}));

            datasets.push(clonePurple);
        }

        return {datasets};
    }

    function buildOptions() {
        const opts = JSON.parse(JSON.stringify(defaultOptions));

        opts.scales.xAxes[0].time.unit = unit;
        opts.scales.xAxes[0].time.parser = unitParser;
        opts.scales.xAxes[0].time.tooltipFormat = unitTooltip;

        return opts;
    }

    // const hasPhysical = physicalData && Object.keys(physicalData).some(k => physicalData[k] > 0);
    const hasPhysical = false;

    return (
        <Box
            sx={{
                flex: 1,
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: `1px solid ${greyishBlue100}`,
                p: isMobile ? '16px' : '20px',
                minWidth: 0,
            }}
        >
            <div style={{ fontWeight: 600, color: greyishBlue600, fontSize: "16px" }}>
                {title}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                {kpiValue && (
                    <div style={{
                        fontWeight: 700,
                        color: black500,
                        fontSize: isMobile ? '22px' : '24px',
                        lineHeight: 1.2
                    }}>
                        {kpiValue}
                    </div>
                )}
                <ChartLegend
                    onlineLegend={onlineLegend}
                    physicalLegend={physicalLegend}
                    showPhysical={hasPhysical}
                />
            </div>
            {subtitle && (
                <div style={{
                    color: greyishBlue600,
                    fontSize: isMobile ? '11px' : '14px',
                    marginTop: "2px"
                }}>
                    {subtitle}
                </div>
            )}
            <Box sx={{ mt: 2, position: "relative", minHeight: 200 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Line
                        data={buildDatasets()}
                        options={buildOptions()}
                        width={400}
                        height={200}
                    />
                )}
            </Box>
        </Box>
    );
}

SalesLineChart.propTypes = {
    title: PropTypes.node.isRequired,
    kpiValue: PropTypes.node,
    subtitle: PropTypes.node,
    onlineData: PropTypes.object,
    physicalData: PropTypes.object,
    onlineLegend: PropTypes.string,
    physicalLegend: PropTypes.string,
    unit: PropTypes.oneOf(["day", "month", "year"]).isRequired,
    unitParser: PropTypes.oneOf(["YYYY", "YYYYMM", "YYYYMMDD"]).isRequired,
    unitTooltip: PropTypes.oneOf(["YYYY", "MMMM YYYY", "D MMMM YYYY"]).isRequired,
    loading: PropTypes.bool,
};

SalesLineChart.defaultProps = {
    onlineData: {},
    physicalData: {},
};

export default SalesLineChart;
